import * as React from 'react';
import { useId, useRef, useState } from 'react';
import {
  root as rootCls,
  tabList as tabListCls,
  tabButton as tabButtonCls,
  panel as panelCls,
} from './Tabs.css';

export type TabsVariant = 'underline' | 'lozenge';

export interface TabItem {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface TabsProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'children'
> {
  items: TabItem[];
  /** Controlled active tab. */
  value?: string;
  /** Uncontrolled initial active tab. Defaults to the first non-disabled item. */
  defaultValue?: string;
  onChange?: (value: string) => void;
  variant?: TabsVariant;
  /** Render the matching panel content for the active tab. */
  children?: (active: string) => React.ReactNode;
  'aria-label'?: string;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * Tabs — accessible tab list (`role="tablist"`) with WAI-ARIA-compliant
 * keyboard nav. `items` is data-driven; pass a `children` render
 * function `(active) => ReactNode` to render the matching panel inline.
 *
 * Keyboard:
 *   - ← / ↑ — previous enabled tab
 *   - → / ↓ — next enabled tab
 *   - Home / End — first / last enabled
 *   - Tab key follows the standard "roving tabindex" — focus moves to
 *     the active tab; arrow keys then traverse within the list.
 *
 * Two visual variants — `underline` (the default thick-bottom-border
 * tab strip) and `lozenge` (segmented control style).
 */
export function Tabs({
  items,
  value,
  defaultValue,
  onChange,
  variant = 'underline',
  children,
  'aria-label': ariaLabel,
  ref,
  className,
  ...rest
}: TabsProps) {
  const initial = defaultValue ?? items.find((i) => !i.disabled)?.value ?? items[0]?.value ?? '';
  const [internal, setInternal] = useState(initial);
  const isControlled = value !== undefined;
  const active = isControlled ? value : internal;
  const tabIdPrefix = useId();
  const listRef = useRef<HTMLDivElement | null>(null);

  const select = (v: string) => {
    if (!isControlled) setInternal(v);
    onChange?.(v);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const enabled = items.filter((i) => !i.disabled);
    const i = enabled.findIndex((it) => it.value === active);
    if (i === -1) return;
    let next = i;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % enabled.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
      next = (i - 1 + enabled.length) % enabled.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = enabled.length - 1;
    else return;
    e.preventDefault();
    // i !== -1 above guarantees enabled.length > 0 and `next` is in range.
    const target = enabled[next]!;
    select(target.value);
    const btn = listRef.current?.querySelector<HTMLButtonElement>(
      `[data-tab-value="${target.value}"]`,
    );
    btn?.focus();
  };

  return (
    <div ref={ref} className={[rootCls, className].filter(Boolean).join(' ')} {...rest}>
      <div
        ref={listRef}
        role="tablist"
        aria-label={ariaLabel}
        className={tabListCls({ variant })}
        onKeyDown={onKeyDown}
        // The WAI-ARIA tablist receives keyboard events but never holds focus
        // itself — focus lives on the active Tab button. tabIndex=-1 makes it
        // programmatically focusable while keeping it out of the tab order.
        tabIndex={-1}
      >
        {items.map((item) => {
          const selected = item.value === active;
          return (
            <button
              key={item.value}
              type="button"
              role="tab"
              id={`${tabIdPrefix}-tab-${item.value}`}
              aria-controls={`${tabIdPrefix}-panel-${item.value}`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              disabled={item.disabled}
              data-tab-value={item.value}
              onClick={() => !item.disabled && select(item.value)}
              className={tabButtonCls({ variant, active: selected })}
            >
              {item.label}
            </button>
          );
        })}
      </div>
      {children && (
        <div
          role="tabpanel"
          className={panelCls}
          id={`${tabIdPrefix}-panel-${active}`}
          aria-labelledby={`${tabIdPrefix}-tab-${active}`}
        >
          {children(active)}
        </div>
      )}
    </div>
  );
}
Tabs.displayName = 'Tabs';
